import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ClipboardCheck,
  Download,
  Edit2,
  Eye,
  Link2,
  Megaphone,
  MoreHorizontal,
  PackageCheck,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ProgramContactStrip } from "./ProgramContactStrip";
import { apiClient } from "../../App";
import { toast } from "sonner";

// Status is carried by a single dot, never by a tinted button or a loud
// background — "color is for status only" from design_guidelines.json.
// Every stage in PROGRAM_STAGES must have an entry here; unknown stages
// fall back to neutral.
const STAGE_DOT = {
  Queued: "bg-[#8A8783]",
  Intake: "bg-[#8A8783]",
  Kickoff: "bg-[#3D6355]",
  Production: "bg-[#B96A43]",
  Revisions: "bg-[#3D6355]",
  "QA/Final": "bg-[#3D6355]",
  "On Hold": "bg-[#C24A2E]",
  Delivered: "bg-[#2E7D55]",
  // Full-contrast, not a hardcoded near-black — the app has a dark theme and
  // #000000 would disappear against it.
  Closed: "bg-foreground",
};

/** Neutral pill with a status dot. One shape for every piece of metadata. */
function StatusPill({ dotClass, children, title, testId }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-foreground"
      title={title}
      data-testid={testId}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
      {children}
    </span>
  );
}

/**
 * Quiet secondary action — a filled grey pill, no border, no shadow.
 * Pass `to` to render as a router link (via asChild, so we never nest a
 * <button> inside an <a>).
 */
function QuietAction({ icon: Icon, label, testId, to }) {
  const className =
    "h-9 rounded-full bg-secondary px-3.5 text-[13px] font-medium text-foreground hover:bg-border/70 sm:px-4";
  const inner = (
    <>
      <Icon className="w-4 h-4 sm:mr-1.5" />
      <span className="hidden sm:inline">{label}</span>
    </>
  );

  return (
    <Button asChild variant="ghost" size="sm" className={className} data-testid={testId}>
      <Link to={to}>{inner}</Link>
    </Button>
  );
}

export function ProgramHero({
  program,
  programId,
  isAdmin,
  hasAgreement,
  marketingModule,
  canManageMarketing,
  onMarketingUpdate,
  onRefresh,
  onEdit,
  onDelete,
  onCloseDeliver,
  onViewAgreement,
  onDownloadAgreement,
  onReplaceAgreement,
}) {
  const stage = program?.stage || "Intake";
  const marketingIncluded = !!marketingModule?.is_included;
  const kickoffOutstanding = marketingIncluded && !marketingModule?.kickoff_complete;

  const handleKickoffComplete = async () => {
    try {
      const res = await apiClient.post(`/programs/${programId}/marketing/kickoff-complete`);
      onMarketingUpdate?.({
        kickoff_complete: true,
        marketing_status: res.data?.marketing_status,
        kickoff_completed_at: res.data?.kickoff_completed_at,
      });
      toast.success("Marketing kickoff marked complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark kickoff complete");
    }
  };

  const handleResendMarketing = async () => {
    try {
      const res = await apiClient.post(`/programs/${programId}/marketing/resend-notification`);
      if (res.data?.sent) {
        toast.success(`Notified Matheus (email: ${res.data.email_status})`);
      } else {
        toast.error(`Resend failed: ${res.data?.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend notification");
    }
  };

  return (
    <div data-testid="program-hero">
      {/* Breadcrumb — quiet, small, gets out of the way */}
      <Link
        to="/programs"
        className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Programs
      </Link>

      {/* Title block */}
      <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
        <div className="min-w-0 flex-1">
          <h1
            className="text-[28px] md:text-[34px] leading-[1.1] font-light tracking-[-0.02em] text-foreground"
            style={{ fontFamily: 'var(--font-sans)' }}
          >
            {program.program_name}
          </h1>

          {/* Metadata row — stage and marketing state. All one shape. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <StatusPill dotClass={STAGE_DOT[stage] || STAGE_DOT.Intake} testId="hero-stage-pill">
              {stage}
            </StatusPill>
            {kickoffOutstanding && (
              <StatusPill
                dotClass="bg-[#B96A43]"
                title="This program has marketing scope. Kickoff has not yet been completed."
                testId="marketing-kickoff-required-badge"
              >
                Marketing kickoff due
              </StatusPill>
            )}
            {marketingIncluded && marketingModule?.kickoff_complete && marketingModule?.marketing_status && (
              <StatusPill
                dotClass="bg-[#2E7D55]"
                title="Marketing pipeline status"
                testId="marketing-pipeline-status-badge"
              >
                Marketing · {marketingModule.marketing_status}
              </StatusPill>
            )}
          </div>

          {/* Client / contact line */}
          <div className="mt-4">
            <ProgramContactStrip program={program} onUpdate={onRefresh} />
          </div>
        </div>

        {/* Actions — one primary, two quiet, everything else behind ⋯ */}
        <div className="flex flex-wrap items-center gap-2 lg:justify-end lg:flex-nowrap">
          {stage !== "Closed" && (
            <Button
              size="sm"
              onClick={onCloseDeliver}
              className="h-9 rounded-full bg-foreground px-4 text-[13px] font-medium text-background hover:bg-foreground/90"
              data-testid="close-deliver-btn"
              title="Close this project, publish it to the portfolio, and notify the team"
            >
              <PackageCheck className="w-4 h-4 mr-1.5" />
              Close &amp; Deliver
            </Button>
          )}

          {/* One entry point. "Client Checklists" used to sit beside this
              pointing at a separate page for the same project's file list; the
              tracker carries that list now, on its Collect tab. */}
          <QuietAction
            icon={Link2}
            label="Client Tracker"
            testId="client-tracker-btn"
            to={`/programs/${programId}/tracker`}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 rounded-full bg-secondary p-0 text-foreground hover:bg-border/70"
                data-testid="program-actions-menu-btn"
                title="More actions"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {kickoffOutstanding && canManageMarketing && (
                <>
                  <DropdownMenuItem
                    onClick={handleKickoffComplete}
                    data-testid="mark-marketing-kickoff-complete-btn"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-2" /> Mark kickoff complete
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              <DropdownMenuItem onClick={onEdit} data-testid="edit-program-btn">
                <Edit2 className="w-4 h-4 mr-2" /> Edit project
              </DropdownMenuItem>

              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                    Agreement
                  </DropdownMenuLabel>
                  {hasAgreement ? (
                    <>
                      <DropdownMenuItem onClick={onViewAgreement} data-testid="view-agreement-menu-item">
                        <Eye className="w-4 h-4 mr-2" /> View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={onDownloadAgreement}>
                        <Download className="w-4 h-4 mr-2" /> Download
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={onReplaceAgreement}>
                        <Upload className="w-4 h-4 mr-2" /> Replace
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem onClick={onReplaceAgreement} data-testid="upload-agreement-btn">
                      <Upload className="w-4 h-4 mr-2" /> Upload agreement
                    </DropdownMenuItem>
                  )}
                </>
              )}

              {marketingIncluded && isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleResendMarketing}
                    data-testid="resend-marketing-notification-btn"
                  >
                    <Megaphone className="w-4 h-4 mr-2" /> Resend to Matheus
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive focus:text-destructive"
                data-testid="delete-program-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

export default ProgramHero;
